import type { Permission } from "@/shared/types/permission.types";
import type { UserEntity } from "@/shared/types/user.entity";

/**
 * The permission primitives every affordance check is built on.
 *
 * `user.scopes` carries the RBAC-effective permissions since the backend started projecting them onto that
 * field, so these read the same set the guards enforce. They are DEFAULT DENY: a missing user, a missing
 * `scopes` array or an unmatched permission all answer `false`. That is deliberate — the module helpers that
 * wrap these used to end in `return true`, which showed create/edit/delete buttons to read-only members and
 * let the API reject them with a 401.
 *
 * These gate affordances, never access. The security boundary is the backend, surfaced by the per-module
 * 401/403 latch.
 */

export function getPermissions(user: UserEntity | null | undefined): readonly Permission[] {
  return user?.scopes ?? [];
}

/** Does the user hold this exact permission? */
export function can(user: UserEntity | null | undefined, permission: Permission): boolean {
  return getPermissions(user).includes(permission);
}

/**
 * Does the user hold ANY of these? An empty list answers `false`: "no permission required" is expressed by
 * not calling this at all, not by passing nothing.
 */
export function canAny(user: UserEntity | null | undefined, permissions: readonly Permission[]): boolean {
  if (permissions.length === 0) return false;
  const held = getPermissions(user);
  return permissions.some((permission) => held.includes(permission));
}
